import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../services/api";

interface Transaction {
  id: string;
  wallet_id: string;
  type: string;
  amount: string;
  balance_after: string;
  description: string;
  reference_id: string | null;
  created_at: string;
}

interface TransactionsResponse {
  transactions: Transaction[];
  total: number;
  limit: number;
  offset: number;
}

export default function AdminTransactionsPage() {
  const [filters, setFilters] = useState({
    type: "",
  });
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "transactions", filters, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });
      if (filters.type) params.append("type", filters.type);

      const response = await api.get<{ success: boolean; data: TransactionsResponse }>(
        `/admin/transactions?${params.toString()}`
      );
      return response.data.data;
    },
  });

  const getTypeBadge = (type: string) => {
    const badges: Record<string, string> = {
      deposit: "bg-green-500/20 text-green-400",
      withdrawal: "bg-red-500/20 text-red-400",
      survey_funding: "bg-blue-500/20 text-blue-400",
      reward_earned: "bg-purple-500/20 text-purple-400",
      platform_fee: "bg-yellow-500/20 text-yellow-400",
      refund: "bg-gray-500/20 text-gray-400",
      sign_up_bonus: "bg-pink-500/20 text-pink-400",
    };
    return badges[type] || "bg-gray-500/20 text-gray-400";
  };

  const formatType = (type: string) => {
    return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Transactions</h1>
        <p className="text-gray-400 mt-2">
          {data?.total || 0} total transactions
        </p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => {
                setFilters({ ...filters, type: e.target.value });
                setPage(0);
              }}
              className="w-full input"
            >
              <option value="">All Types</option>
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="survey_funding">Survey Funding</option>
              <option value="reward_earned">Reward Earned</option>
              <option value="platform_fee">Platform Fee</option>
              <option value="refund">Refund</option>
              <option value="sign_up_bonus">Sign Up Bonus</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading transactions...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-border">
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Type</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Amount</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Balance After</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Description</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Reference</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {data?.transactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-dark-border hover:bg-dark-700">
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeBadge(tx.type)}`}>
                        {formatType(tx.type)}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`font-medium ${parseFloat(tx.amount) >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {parseFloat(tx.amount) >= 0 ? "+" : ""}${parseFloat(tx.amount).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-white">
                      ${parseFloat(tx.balance_after).toFixed(2)}
                    </td>
                    <td className="py-4 px-6 text-gray-300">
                      {tx.description}
                    </td>
                    <td className="py-4 px-6 text-gray-400">
                      {tx.reference_id ? (
                        <span className="text-xs">{tx.reference_id.slice(0, 12)}...</span>
                      ) : (
                        <span className="text-gray-600">-</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-gray-400">
                      {new Date(tx.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!data?.transactions.length && (
              <div className="text-center py-12 text-gray-400">No transactions found</div>
            )}
          </div>
        )}

        {/* Pagination */}
        {data && data.total > limit && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-dark-border">
            <div className="text-gray-400 text-sm">
              Showing {page * limit + 1} to {Math.min((page + 1) * limit, data.total)} of {data.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-4 py-2 bg-dark-700 text-white rounded hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * limit >= data.total}
                className="px-4 py-2 bg-dark-700 text-white rounded hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
